import { alpha } from "@mui/material/styles";
import type { QueueConfigSummary } from "@situate/contracts/runtime";
import type { QueueRagStatus } from "@/features/deployment-dashboard/live-view/model/queue";
import type { GeoDirectionalLine, GeoScatterBoundary } from "@/shared/charts";
import { transformThroughputLineGeometry } from "@/features/deployment-dashboard/widgets/panels/throughput-line-map/throughput-line-map.utils";
import { worldToFloorplanPixel } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { ThroughputLineOption } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import type { WorldToPixelTransformAsset } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { QueueQualityReason } from "@situate/contracts/runtime";
import {
  formatQueueWaitShort,
  isNearEmptyQueueWait,
} from "@/features/deployment-dashboard/shared/queue/queue-wait";

interface QueueMapLineColors {
  /** Shared entrance/exit gate color; direction is conveyed by the IN/OUT label and arrow, not by hue. */
  gate: string;
}

interface QueueMapBoundaryColors {
  byStatus: ReadonlyMap<QueueRagStatus, string>;
  unavailable: string;
  /** Text/background for the queue's KPI chip, independent of its RAG status color. */
  labelText: string;
  labelBackground: string;
}

/** Live people-count and wait-time metrics for a single queue, shown in its map KPI chip. */
export interface QueueMapMetrics {
  peopleInQueue: number | null;
  estimatedWaitMinutes: number | null;
  waitDataStatus?: "current" | "stale" | "unavailable";
  waitQualityReasons?: QueueQualityReason[];
}

/** Moves the selected queue to the front so it claims label-placement priority (see calculateGateLabelPlacements). */
function orderBySelectedFirst(
  queueConfigs: QueueConfigSummary[],
  selectedQueueId: string | null
): QueueConfigSummary[] {
  if (selectedQueueId === null) return queueConfigs;
  const selected = queueConfigs.filter((queue) => queue.queue_id === selectedQueueId);
  const rest = queueConfigs.filter((queue) => queue.queue_id !== selectedQueueId);
  return [...selected, ...rest];
}

function formatQueueMetricsLine(metrics: QueueMapMetrics | undefined): string {
  const peopleText =
    metrics?.peopleInQueue == null
      ? "– people"
      : `${metrics.peopleInQueue} ${metrics.peopleInQueue === 1 ? "person" : "people"}`;
  const waitText = formatQueueWaitShort(
    metrics?.estimatedWaitMinutes ?? null,
    metrics != null &&
      isNearEmptyQueueWait({
        value: metrics.estimatedWaitMinutes,
        dataStatus: metrics.waitDataStatus ?? "unavailable",
        qualityReasons: metrics.waitQualityReasons,
      })
  );
  return `${peopleText} · ${waitText}`;
}

function queueBoundaryLine(
  queue: QueueConfigSummary,
  role: "entrance" | "exit",
  boundaryIndex: number,
  lineId: string,
  directionId: string | undefined,
  linesById: ReadonlyMap<string, ThroughputLineOption>,
  transform: WorldToPixelTransformAsset,
  colors: QueueMapLineColors,
  isSelected: boolean
): GeoDirectionalLine | null {
  const line = linesById.get(lineId);
  if (!line) return null;

  const geometry = transformThroughputLineGeometry(
    line.line_id === "security-queue-exit"
      ? {
          ...line.line_geometry,
          world_end: {
            ...line.line_geometry.world_end,
            y: line.line_geometry.world_start.y,
          },
        }
      : line.line_geometry,
    transform
  );
  if (line.line_id === "security-queue-exit") {
    geometry.end[1] = geometry.start[1];
  }
  const isPositiveDirection =
    directionId === "positive" ||
    (directionId !== "negative" && line.positive_direction.direction_id === directionId);

  return {
    id: `queue:${queue.queue_id}:${role}:${boundaryIndex}:${line.line_id}`,
    ...geometry,
    color: colors.gate,
    width: isSelected ? 5 : 3,
    positiveColor: colors.gate,
    negativeColor: colors.gate,
    label: role === "entrance" ? "IN" : "OUT",
    arrowSide: isPositiveDirection ? "positive" : "negative",
    arrowSize: 8,
    arrowStyle: "chevron",
    opacity: isSelected ? 1 : 0.7,
    // Independent of the (now neutral) line color, so IN/OUT text stays legible.
    labelColor: "#1a1a1a",
    labelBackgroundColor: "rgba(255, 255, 255, 0.55)",
    emphasis: isSelected ? { width: 5, opacity: 1 } : undefined,
  };
}

function queueBoundaryLines(
  queue: QueueConfigSummary,
  role: "entrance" | "exit",
  linesById: ReadonlyMap<string, ThroughputLineOption>,
  transform: WorldToPixelTransformAsset,
  colors: QueueMapLineColors,
  isSelected: boolean
): GeoDirectionalLine[] {
  const boundaries = queue.boundaries;
  if (!boundaries) return [];

  const pluralBoundaries = role === "entrance" ? boundaries.entrances : boundaries.exits;
  const singleBoundary = role === "entrance" ? boundaries.entrance : boundaries.exit;
  const boundaryEntries =
    pluralBoundaries !== undefined
      ? pluralBoundaries.map((boundary) => ({
          lineId: boundary.line_id,
          directionId:
            role === "entrance"
              ? boundary.queue_entry_direction_id
              : boundary.queue_exit_direction_id,
        }))
      : singleBoundary === undefined
        ? []
        : [{ lineId: singleBoundary.line_id, directionId: singleBoundary.direction_id }];

  return boundaryEntries.flatMap((boundary, index) => {
    const line = queueBoundaryLine(
      queue,
      role,
      index,
      boundary.lineId,
      boundary.directionId,
      linesById,
      transform,
      colors,
      isSelected
    );
    return line === null ? [] : [line];
  });
}

/** Maps configured queue areas into floor-plan pixel space, with a KPI chip centered in each polygon. */
export function deriveQueueMapBoundaries(
  transform: WorldToPixelTransformAsset | undefined,
  queueConfigs: QueueConfigSummary[],
  selectedQueueId: string | null,
  queueStatuses: ReadonlyMap<string, QueueRagStatus | null>,
  queueMetrics: ReadonlyMap<string, QueueMapMetrics>,
  colors: QueueMapBoundaryColors
): GeoScatterBoundary[] {
  if (!transform) return [];

  return orderBySelectedFirst(queueConfigs, selectedQueueId).map((queue) => {
    const selected = queue.queue_id === selectedQueueId;
    const status = queueStatuses.get(queue.queue_id) ?? null;
    const color =
      status === null ? colors.unavailable : (colors.byStatus.get(status) ?? colors.unavailable);
    return {
      id: `queue:${queue.queue_id}`,
      name: queue.queue_display_name,
      tooltip: queue.queue_display_name,
      points: queue.polygon.vertices.map((vertex) => {
        const pixel = worldToFloorplanPixel(transform, vertex.easting_m, vertex.northing_m);
        return [pixel.x, pixel.y];
      }),
      style: {
        fill: alpha(color, selected ? 0.14 : 0.06),
        stroke: selected ? color : alpha(color, 0.7),
        strokeWidth: selected ? 2 : 1.5,
        lineDash: selected ? "dotted" : "solid",
        animated: selected,
      },
      label: {
        text: `${queue.queue_display_name}\n${formatQueueMetricsLine(queueMetrics.get(queue.queue_id))}`,
        color: colors.labelText,
        backgroundColor: colors.labelBackground,
      },
    };
  });
}

/** Maps configured queue entry and exit lines into floor-plan pixel space. */
export function deriveQueueMapLines(
  transform: WorldToPixelTransformAsset | undefined,
  queueConfigs: QueueConfigSummary[],
  throughputLines: ThroughputLineOption[],
  selectedQueueId: string | null,
  colors: QueueMapLineColors
): GeoDirectionalLine[] {
  if (!transform) return [];

  const linesById = new Map(throughputLines.map((line) => [line.line_id, line]));
  return orderBySelectedFirst(queueConfigs, selectedQueueId).flatMap((queue) => {
    const isSelected = queue.queue_id === selectedQueueId;
    return [
      ...queueBoundaryLines(queue, "entrance", linesById, transform, colors, isSelected),
      ...queueBoundaryLines(queue, "exit", linesById, transform, colors, isSelected),
    ];
  });
}
